import NiceAvatar from 'react-nice-avatar';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import fromUnixTime from 'date-fns/fromUnixTime';
import { Avatar } from '@chakra-ui/react';
import { differenceInSeconds, isFuture, isPast } from 'date-fns';

import {
  ChatRoomSetting,
  EventTypeFromFirebaseUsers,
  UserTypeFromFirebase,
} from './types';

export function redeemGenerator(length: number): string {
  const possible = 'ABCDEFGHJKMNPQRSTVWXYZabcdefghjkmnpqrstvwxyz23456789';
  let text = '';

  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
}

export function validateEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function getStreamStatusColor(status: string) {
  switch (status) {
    case 'idle':
      return 'yellow';
    case 'active':
      return 'green';
    case 'disconnected':
      return 'red';
    default:
      return 'grey';
  }
}

export function getEventStatusString(status: string) {
  switch (status) {
    case 'upcoming':
      return 'Upcoming';
    case 'ongoing':
      return 'Ongoing';
    case 'past':
      return 'Past';
    default:
      return status;
  }
}

export type EventStatusReturns = {
  status: 'upcoming' | 'ongoing' | 'past' | 'unknown';
  timeOffset: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  startedTimeOffset: number;
  endedTimeOffset: number;
};

export function getEventStatus({
  startedAt,
  endedAt,
}: {
  startedAt: number;
  endedAt: number;
}): EventStatusReturns {
  const startInFuture = isFuture(fromUnixTime(startedAt));
  const started = isPast(fromUnixTime(startedAt));
  const endInFuture = isFuture(fromUnixTime(endedAt));
  const ended = isPast(fromUnixTime(endedAt));

  let status: EventStatusReturns['status'];

  if (startInFuture) {
    status = 'upcoming';
  } else if (started && endInFuture) {
    status = 'ongoing';
  } else if (ended) {
    status = 'past';
  } else {
    status = 'unknown';
  }
  let hours, minutes, seconds;
  const currentTime = new Date();
  switch (status) {
    case 'ongoing': {
      hours = differenceInHours(fromUnixTime(endedAt), currentTime);
      minutes = differenceInMinutes(fromUnixTime(endedAt), currentTime);
      seconds = differenceInSeconds(fromUnixTime(endedAt), currentTime);
      break;
    }
    case 'upcoming': {
      hours = differenceInHours(currentTime, fromUnixTime(startedAt));
      minutes = differenceInMinutes(currentTime, fromUnixTime(startedAt));
      seconds = differenceInSeconds(currentTime, fromUnixTime(startedAt));
      break;
    }
    case 'past': {
      hours = -1;
      minutes = -1;
      seconds = -1;
      break;
    }
    default: {
      hours = -1;
      minutes = -1;
      seconds = -1;
    }
  }

  return {
    status,
    timeOffset: {
      hours,
      minutes,
      seconds,
    },
    startedTimeOffset:
      fromUnixTime(startedAt).getTime() - currentTime.getTime(),
    endedTimeOffset: fromUnixTime(endedAt).getTime() - currentTime.getTime(),
  };
}

export function getTimeOffset({
  startedAt,
  endedAt,
}: {
  startedAt: number;
  endedAt: number;
}): { hours: number; minutes: number } {
  const diffHours = differenceInHours(
    fromUnixTime(endedAt),
    fromUnixTime(startedAt)
  );
  const diffMinutes = differenceInMinutes(
    fromUnixTime(endedAt),
    fromUnixTime(startedAt)
  );
  return {
    hours: diffHours,
    minutes: diffMinutes,
  };
}

export const getGoogleCalendarUrl = ({
  text,
  startDate,
  endDate,
  description,
}: {
  text: string;
  startDate: Date;
  endDate: Date;
  description: string;
}) => {
  const formattedText = text.split(' ').join('+');

  const formattedStartDate = startDate
    .toISOString()
    .replace('.000', '') // we need to trim the millisecond for iOS date format
    .replace(/[-:.]/g, '');
  const formattedEndDate = endDate
    .toISOString()
    .replace('.000', '') // we need to trim the millisecond for iOS date format
    .replace(/[-:.]/g, '');
  const formattedDescription = description.split(' ').join('+');

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${formattedText}&dates=${formattedStartDate}/${formattedEndDate}&details=${formattedDescription}&sf=true&output=xml`;
};

export function getDisplayNameFromUser(
  user: Partial<ChatRoomSetting> & Partial<UserTypeFromFirebase>
) {
  const { username } = user;

  if (username) {
    return username;
  }

  return '';
}

function getUnitLength(size: string) {
  switch (size) {
    case 'xs':
      return '2rem';
    case 'sm':
      return '3rem';
    case 'md':
      return '4rem';
    case 'xl':
      return '8rem';
    default:
      return '4rem';
  }
}

export function getAvatar({
  user,
  size,
}: {
  user: Partial<ChatRoomSetting> & Partial<UserTypeFromFirebase>;
  size: 'xl' | 'md' | 'sm' | 'xs';
}) {
  const { profilePic, avatar } = user;
  const name = getDisplayNameFromUser(user);

  const unitLength = getUnitLength(size);

  if (profilePic) {
    return (
      <Avatar
        width={unitLength}
        height={unitLength}
        name={name}
        src={profilePic}
      />
    );
  }

  if (avatar) {
    return (
      <NiceAvatar
        style={{ width: unitLength, height: unitLength }}
        {...avatar}
      />
    );
  }

  return <Avatar size={size} name={name} />;
}

export function getAvailableEventIds(
  events: Record<string, EventTypeFromFirebaseUsers>
) {
  const allEvents = Object.entries(events).map(([key, value]) => ({
    ...value,
    id: key,
  }));

  const availableEventsObject = allEvents.filter(
    (event: EventTypeFromFirebaseUsers) => !event.deletedAt
  );

  const availableEventIds = availableEventsObject.map((event) => event.id);

  return availableEventIds;
}
